<template>
    <div class="klok-container relative size-full">
        <div
            id="klok-view"
            class="absolute z-0 size-full outline-none"
        />
        <!-- <div
            class="absolute left-4 top-4 z-50 border border-black-text bg-white px-6 py-2 font-probold shadow-md"
        >
            {{
                useLocaleValue(
                    mainStore.getPageInfo("mapy-katalog-lokalit"),
                    "title"
                )
            }}
        </div> -->
        <div
            class="absolute left-0 top-0 z-40 w-full border-b border-black-text bg-white px-3 py-2 text-center font-promedium opacity-0 duration-500 md:left-4 md:top-4 md:w-auto md:border md:px-6"
            :class="{
                'pointer-events-none -translate-x-1/2 opacity-0 duration-200':
                    store.selectedLocality || store.selectedMetrika,
                'pointer-events-auto translate-x-0 opacity-100 duration-500':
                    !store.selectedLocality && !store.selectedMetrika,
            }"
        >
            {{ $t("klok.tooltip_main") }}
        </div>
        <div
            class="absolute left-0 top-0 z-40 w-full border-b border-black-text bg-white px-3 py-2 text-center font-promedium md:left-4 md:top-4 md:w-auto md:border md:px-6"
            :class="{
                'pointer-events-none -translate-x-1/2 opacity-0 duration-200':
                    !store.selectedLocality || store.selectedMetrika,
                'pointer-events-auto translate-x-0 opacity-100 duration-500':
                    store.selectedLocality && !store.selectedMetrika,
            }"
        >
            {{ $t("klok.tooltip_main_2") }}
        </div>
        <ClientOnly>
            <KlokMapControls v-if="store.viewReady" />
        </ClientOnly>
    </div>
</template>

<script setup lang="ts">
import MapView from "@arcgis/core/views/MapView"
import Map from "@arcgis/core/Map"
import Point from "@arcgis/core/geometry/Point"
import Polygon from "@arcgis/core/geometry/Polygon"
import Basemap from "@arcgis/core/Basemap"
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer"
import FeatureLayer from "@arcgis/core/layers/FeatureLayer"
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer"
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol"
import LabelClass from "@arcgis/core/layers/support/LabelClass"
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils"
import Popup from "@arcgis/core/widgets/Popup"
import Handles from "@arcgis/core/core/Handles"

const store = useKlokStore()
const mainStore = useMainStore()
const { getDictionary } = useKlokDictionaries()
const { locale } = useI18n()

declare global {
    interface Window {
        klok: {
            view: MapView
            map: Map
        }
    }
}

let layerView: __esri.FeatureLayerView | undefined = undefined
const defaultRenderer = new SimpleRenderer({
    symbol: new SimpleFillSymbol({
        color: [0, 0, 0, 0],
        outline: {
            width: 1,
            color: [0, 0, 0, 0.5],
        },
    }),
})

const cityBorderLayer = new FeatureLayer({
    url: store.cityBordersUrl,
    id: "hranice",
    renderer: new SimpleRenderer({
        symbol: new SimpleFillSymbol({
            color: [0, 0, 0, 0],
            outline: {
                width: 3,
                color: [0, 0, 0, 1],
            },
        }),
    }),
    popupEnabled: false,
})

const klokLayer = new FeatureLayer({
    url: store.klokUrl,
    id: "klok",
    outFields: ["*"],
    renderer: defaultRenderer,
    labelingInfo: [
        new LabelClass({
            symbol: {
                type: "text",
                color: "black",
                haloColor: "white",
                haloSize: 1,
                font: {
                    size: 11,
                    weight: "bold",
                    family: "sans-serif",
                },
            },
            labelExpressionInfo: {
                expression: store.selectedMetrika
                    ? getLabelExpression(store.selectedMetrika)
                    : "$feature.CISLO + TextFormatting.NewLine + $feature.NAZEV",
            },
            minScale: 50000,
        }),
    ],
})

const handles = new Handles()

klokLayer.on("layerview-create", async e => {
    layerView = e.layerView as __esri.FeatureLayerView
    await getAllLocalitiesForSearch()
    if (store.selectedMetrika) {
        setMertrikaSettings(store.selectedMetrika)
    }
    setLocalityFromUrlParams()
})

watch(
    () => store.selectedMetrika,
    metrika => {
        if (metrika) {
            setMertrikaSettings(metrika)
        } else {
            klokLayer.renderer = defaultRenderer
            if (
                klokLayer &&
                klokLayer.labelingInfo &&
                klokLayer.labelingInfo[0] &&
                klokLayer.labelingInfo[0].labelExpressionInfo
            ) {
                klokLayer.labelingInfo[0].labelExpressionInfo.expression =
                    "$feature.CISLO + TextFormatting.NewLine + $feature.NAZEV"
                store.layerOpacity = 1
            }
        }
    }
)

watch(locale, () => {
    if (store.selectedMetrika) {
        if (
            klokLayer &&
            klokLayer.labelingInfo &&
            klokLayer.labelingInfo[0] &&
            klokLayer.labelingInfo[0].labelExpressionInfo
        ) {
            klokLayer.labelingInfo[0].labelExpressionInfo.expression =
                getLabelExpression(store.selectedMetrika)
        }
    } else {
        if (
            klokLayer &&
            klokLayer.labelingInfo &&
            klokLayer.labelingInfo[0] &&
            klokLayer.labelingInfo[0].labelExpressionInfo
        ) {
            klokLayer.labelingInfo[0].labelExpressionInfo.expression =
                "$feature.CISLO + TextFormatting.NewLine + $feature.NAZEV"
        }
    }
})

function getLabelExpression(metrika: KlokMetrikaItem) {
    const attrNames = metrika.NAZEV_UDAJE_DATA.split(",")
    if (attrNames.length === 1) {
        const jednotka = useLocaleValue(metrika, "JEDNOTKA_ZOBRAZ")
        const dictionaryAttrs = [
            "TYP_STR",
            "ZASTAVENOST",
            "STABILITA",
            "PREVLAD_VYUZ",
            "PODL",
        ]
        if (dictionaryAttrs.includes(metrika.NAZEV_UDAJE_DATA)) {
            const dictionary = getDictionary(metrika.NAZEV_UDAJE_DATA)
            if (!dictionary) return `$feature.${metrika.NAZEV_UDAJE_DATA}`
            const mapDict = dictionary.map(el => el.key)
            return `First(Slice(${JSON.stringify(dictionary)}, IndexOf(${JSON.stringify(mapDict)},$feature.${metrika.NAZEV_UDAJE_DATA}))).value_cz`
        }
        if (metrika.DESETINNA_MISTA) {
            return `Concatenate([Text(Round($feature.${metrika.NAZEV_UDAJE_DATA},${metrika.DESETINNA_MISTA})),"${jednotka}"], " ")`
        }
        return `Concatenate([Text($feature.${metrika.NAZEV_UDAJE_DATA}),"${jednotka}"], " ")`
    }
    const attrName = attrNames[0]
    const rounded = metrika.DESETINNA_MISTA.split(",")[0]
    const jednotka = useLocaleValue(metrika, "JEDNOTKA_ZOBRAZ").split(",")[0]
    if (jednotka) {
        return `Concatenate([Text(Round($feature.${attrName},${rounded})),"${jednotka}"], " ")`
    }
    return `Concatenate([Text(Round($feature.${attrName},${rounded})), " %"])`
}

async function getAllLocalitiesForSearch() {
    const query = klokLayer.createQuery()
    query.outFields = ["OBJECTID", "CISLO_TXT", "NAZEV"]
    query.returnGeometry = false
    const result = await klokLayer.queryFeatures(query)
    store.localitiesForSearch = result.features
        .map(f => {
            return {
                id: f.attributes.OBJECTID,
                cislo: f.attributes.CISLO_TXT,
                nazev: f.attributes.NAZEV,
            }
        })
        .sort((a, b) => a.cislo.localeCompare(b.cislo))
    // console.log(store.localitiesForSearch)
}

watch(
    () => store.searchedLocality,
    locality => {
        if (locality) {
            setLocalityFromSearch()
        }
    }
)

function setLocalityFromSearch() {
    klokLayer
        .queryFeatures({
            where: `CISLO_TXT = '${store.searchedLocality}' OR NAZEV = '${store.searchedLocality}'`,
            outFields: ["*"],
            returnGeometry: true,
        })
        .then(result => {
            if (result.features.length > 0 && result.features[0]) {
                store.selectedLocality = result.features[0]
                    .attributes as KlokLokalitaItem
                if (layerView) {
                    handles.removeAll()
                    handles.add(layerView.highlight(result.features[0]))
                }
            }
        })
}

function setMertrikaSettings(metrika: KlokMetrikaItem) {
    const renderer = useKlokRenderer(metrika.NAZEV_UDAJE_DATA)
    if (renderer) {
        klokLayer.renderer = renderer
        if (
            klokLayer &&
            klokLayer.labelingInfo &&
            klokLayer.labelingInfo[0] &&
            klokLayer.labelingInfo[0].labelExpressionInfo
        ) {
            klokLayer.labelingInfo[0].labelExpressionInfo.expression =
                getLabelExpression(metrika)
        }
    }
    store.mode = "mesto"
}

function setLocalityFromUrlParams() {
    const params = useRoute().query.lokalita
    if (!params || typeof params !== "string") return
    klokLayer
        .queryFeatures({
            where: `CISLO_TXT = '${params}'`,
            outFields: ["*"],
            returnGeometry: true,
        })
        .then(result => {
            if (result.features.length > 0 && result.features[0]) {
                store.selectedLocality = result.features[0]
                    .attributes as KlokLokalitaItem
                if (layerView) {
                    handles.add(layerView.highlight(result.features[0]))
                }
            }
        })
}

const baseLayer = new VectorTileLayer({
    url: store.basemapUrl,
    effect: "saturate(50%)",
})

const map = new Map({
    basemap: new Basemap({
        baseLayers: [baseLayer],
    }),
    layers: [klokLayer, cityBorderLayer],
})

onMounted(() => {
    window.klok = {
        view: new MapView({
            container: "klok-view",
            map: map,
            scale: 100000,
            center: new Point({
                x: -740031.9014723228,
                y: -1046320.1038946183,
                spatialReference: {
                    wkid: 5514,
                },
            }),
            ui: {
                components: ["attribution"],
            },
            popupEnabled: false,
            popup: new Popup({
                defaultPopupTemplateEnabled: true,
            }),
            /* popup: {
                defaultPopupTemplateEnabled: true,
            }, */
        }),
        map,
    }

    window.klok.view.when(() => {
        store.viewReady = true
    })

    window.klok.view.on("click", async e => {
        store.isFetching = true
        handles.removeAll()
        // store.selectedLocality = null
        const fetchFeaturesResponse =
            await window.klok.view.popup?.fetchFeatures(e)
        const graphics = await fetchFeaturesResponse?.allGraphicsPromise
        if (graphics && graphics.length > 0) {
            store.selectedLocality = (graphics[0] as __esri.Graphic)
                .attributes as KlokLokalitaItem
            if (layerView) {
                handles.add(layerView.highlight(graphics[0] as __esri.Graphic))
                if (mainStore.mobileMode && graphics[0]) {
                    zoomToGraphicMobile(graphics[0])
                }
            }

            // console.log(store.selectedLocality)
            mainStore.mobileLeftPanelOpen = true
        } else {
            store.selectedLocality = null
        }

        store.isFetching = false
    })

    reactiveUtils.watch(
        () => window.klok.view.popup?.selectedFeature,
        feature => {
            if (feature) {
                store.selectedLocality = feature.attributes as KlokLokalitaItem
                // console.log(store.selectedLocality)
            } else {
                store.selectedLocality = null
            }
        }
    )

    watch(
        () => store.layerOpacity,
        opacity => {
            klokLayer.opacity = opacity
        }
    )

    watch(
        () => store.selectedLocality,
        locality => {
            if (!locality) {
                handles.removeAll()
            }
        }
    )

    watch(
        () => store.mode,
        mode => {
            if (mode === "mesto") {
                klokLayer.definitionExpression = null
            } else {
                klokLayer.definitionExpression =
                    "TYP_STR = " + store.selectedLocality?.TYP_STR
            }
        }
    )
})

function zoomToGraphicMobile(graphic: __esri.Graphic) {
    /* const newPoint = new Polygon({
        x: graphic.geometry.extent.center.x,
        y: graphic.geometry.extent.center.y + window.klok.view.resolution * 100,
        spatialReference: {
            wkid: 5514,
        },
    }) */
    if (!graphic.geometry?.extent) return

    const marginInline = window.innerWidth * 0.2
    const marginBlock = window.innerHeight * 0.2
    const extent = graphic.geometry.extent.clone().expand(1.1)
    const newPoint = new Polygon({
        rings: [
            [
                [extent.xmin - marginInline, extent.ymin + marginBlock],
                [extent.xmax + marginInline, extent.ymin + marginBlock],
                [extent.xmax + marginInline, extent.ymax + marginBlock],
                [extent.xmin - marginInline, extent.ymax + marginBlock],
                [extent.xmin - marginInline, extent.ymin + marginBlock],
            ],
        ],
        spatialReference: extent.spatialReference,
    })

    window.klok.view.goTo(newPoint)
}
</script>

<style scoped>
@import "https://js.arcgis.com/4.30/@arcgis/core/assets/esri/themes/light/main.css";

#klok-view {
    /* &:deep(.esri-view-root) {
        @apply rounded-md shadow-lg;
    } */
    &:deep(.esri-view-user-storage) {
        @apply relative;
    }
    :deep(.esri-attribution) {
        @apply right-auto px-4;
    }
}
</style>
